import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './styles/App.css';
import './styles/fonts.css';
import { bio, reviews } from './content/bio';
import { news } from './content/news';
import { fetchAbout, fetchPressQuotes, fetchNews, fetchTourDates, fetchShopItems, fetchMedia, fetchLyrics } from './lib/airtable';

import AudioPlayer from './components/AudioPlayer';
import SocialLinks from './components/SocialLinks';
import Section from './components/Section';
import MediaContent from './components/MediaContent';
import { SectionColorProvider, useSectionColor } from './context/SectionColorContext';

// Hero Section - Full Screen Image
function HeroSection() {
  return (
    <section className="hero-container">
      <div className="hero-image-container">
        <div className="hero-image-wrapper">
          <img 
            src={process.env.PUBLIC_URL + '/images/landing.jpg'} 
            alt="Sanyu performing live" 
            className="hero-image"
          />
          <figcaption className="hero-credit">
            photograph by jonathan kambskard-bennett
          </figcaption>
        </div>
      </div>
      <TypewriterLyrics />
    </section>
  );
}

// Typewriter Lyrics Component
function TypewriterLyrics() {
  const [currentText, setCurrentText] = useState('');
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lyricsLines, setLyricsLines] = useState([]);

  useEffect(() => {
    fetchLyrics().then(lines => { if (lines.length > 0) setLyricsLines(lines); });
  }, []);

  useEffect(() => {
    if (lyricsLines.length === 0) return;
    const timer = setTimeout(() => {
      const currentLine = lyricsLines[currentLineIndex];
      
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentLine.length) {
          setCurrentText(currentLine.slice(0, currentText.length + 1));
        } else {
          // Finished typing current line, wait then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          // Finished deleting, move to next line
          setIsDeleting(false);
          setCurrentLineIndex((prev) => (prev + 1) % lyricsLines.length);
        }
      }
    }, isDeleting ? 50 : currentText === lyricsLines[currentLineIndex] ? 100 : 100);

        return () => clearTimeout(timer);
  }, [currentText, currentLineIndex, isDeleting, lyricsLines]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      setIsVisible(scrolled < windowHeight * 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="lyrics-overlay">
      <div className="lyrics-text">
        {currentText}
        <span className="cursor">|</span>
      </div>
    </div>
  );
}

// Navigation Bar
function Navigation() {
  const { currentColors } = useSectionColor();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false); // Close mobile menu after navigation
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="navigation desktop-nav">
      <div className="nav-left">
        <button 
          onClick={() => scrollToSection('about')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          about
        </button>
        <button 
          onClick={() => scrollToSection('news')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          news
        </button>
        <button 
          onClick={() => scrollToSection('tour')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          tour dates
        </button>
        </div>
        
      <div className="nav-center">
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
          className="nav-logo"
        >
          <img 
            src={process.env.PUBLIC_URL + '/sanyu.png'} 
            alt="Sanyu Logo" 
            className="navbar-logo"
          />
        </button>
        </div>
        
      <div className="nav-right">
        <button 
          onClick={() => scrollToSection('media')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          media
        </button>
        <button 
          onClick={() => scrollToSection('shop')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          shop
        </button>
        <button 
          onClick={() => scrollToSection('signup')} 
          className="nav-link"
          style={{ color: currentColors.text }}
        >
          sign up
        </button>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <div className="mobile-nav-header">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
            className="mobile-nav-logo"
          >
            <img 
              src={process.env.PUBLIC_URL + '/sanyu.png'} 
              alt="Sanyu Logo" 
              className="navbar-logo"
            />
          </button>
          <button 
            onClick={toggleMobileMenu}
            className="hamburger-menu"
            style={{ color: currentColors.text }}
          >
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
      </nav>

      {/* Mobile Slide Menu */}
      <div className={`mobile-slide-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          <button 
            onClick={() => scrollToSection('about')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            about
          </button>
          <button 
            onClick={() => scrollToSection('news')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            news
          </button>
          <button 
            onClick={() => scrollToSection('tour')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            tour dates
          </button>
          <button 
            onClick={() => scrollToSection('media')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            media
          </button>
          <button 
            onClick={() => scrollToSection('shop')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            shop
          </button>
          <button 
            onClick={() => scrollToSection('signup')} 
            className="mobile-nav-link"
            style={{ color: currentColors.text }}
          >
            sign up
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
}

// Section Content Components
function AboutContent() {
  const [aboutContent, setAboutContent] = useState(null);
  const [pressQuotes, setPressQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadContent() {
      try {
        const [about, quotes] = await Promise.all([
          fetchAbout(),
          fetchPressQuotes(),
        ]);
        
        setAboutContent(about?.content || bio.content);
        setPressQuotes(quotes.length > 0 ? quotes : reviews.map(r => {
          const parts = r.split(' - ');
          return { quote: parts[0], attribution: parts.slice(1).join(' - ') };
        }));
      } catch (error) {
        console.error('Error loading about content:', error);
        setAboutContent(bio.content);
        setPressQuotes(reviews.map(r => {
          const parts = r.split(' - ');
          return { quote: parts[0], attribution: parts.slice(1).join(' - ') };
        }));
      } finally {
        setIsLoading(false);
      }
    }
    loadContent();
  }, []);

  if (isLoading) {
    return <div className="loading-state">loading...</div>;
  }

  return (
    <>
      <div className="bio-content">
        {(aboutContent || bio.content).split('\n\n').map((paragraph, index) => (
          <p key={index} className="bio-paragraph">{paragraph}</p>
        ))}
      </div>
      <div className="reviews">
        {pressQuotes.map((item, index) => (
          <motion.div 
            key={index}
            className="review-container"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.3 }}
          >
            <blockquote className="review-quote">
              "{item.quote}"
            </blockquote>
            <div className="review-attribution">
              {item.attribution}
            </div>
          </motion.div>
        ))}
      </div>
    </>
  );
}

function TourContent() {
  const [tourDates, setTourDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadTourDates() {
      try {
        const dates = await fetchTourDates();
        setTourDates(dates);
      } catch (error) {
        console.error('Error loading tour dates:', error);
        setTourDates([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadTourDates();
  }, []);

  if (isLoading) {
    return <div className="loading-state">loading...</div>;
  }

  return (
    <div className="tour-content">
      {tourDates.length > 0 ? (
        <div className="tour-dates-list">
          {tourDates.map((show, index) => (
            <motion.div 
              key={index}
              className="tour-date-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="tour-date">{show.date}</div>
              <div className="tour-venue">{show.venue}</div>
              <div className="tour-location">{show.location}</div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No tour dates announced yet.</p>
        </div>
      )}
      
      <div className="tour-tickets">
        <a 
          href="https://www.bandsintown.com/a/15570589-sanyu?came_from=253&utm_medium=web&utm_source=city_page&utm_campaign=search_bar"
          target="_blank"
          rel="noopener noreferrer"
          className="tickets-link"
        >
          tickets →
        </a>
      </div>
    </div>
  );
}

function NewsContent() {
  const [newsItems, setNewsItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadNews() {
      try {
        const items = await fetchNews();
        setNewsItems(items.length > 0 ? items : news);
      } catch (error) {
        console.error('Error loading news:', error);
        setNewsItems(news);
      } finally {
        setIsLoading(false);
      }
    }
    loadNews();
  }, []);

  if (isLoading) {
    return <div className="loading-state">loading...</div>;
  }

  return (
    <>
      {newsItems.length > 0 ? (
        <div className="news-list">
          {newsItems.map((item, index) => (
            <motion.div 
              key={index}
              className="news-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
            >
              <div className="news-content">
                <div className="news-date">{item.date}</div>
                <div className="news-title">{item.title}</div>
                <div className="news-description">{item.description}</div>
              </div>
              {item.imageUrl && (
                <div className="news-image-container">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title || 'News image'} 
                    className="news-image"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No news at the moment.</p>
        </div>
      )}
    </>
  );
}

function ShopContent() {
  const [shopItems, setShopItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadShopItems() {
      try {
        const items = await fetchShopItems();
        setShopItems(items);
      } catch (error) {
        console.error('Error loading shop items:', error);
        setShopItems([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadShopItems();
  }, []);

  if (isLoading) {
    return <div className="loading-state">loading...</div>;
  }

  return (
    <div className="shop-content">
      {shopItems.length > 0 ? (
        <>
          <div className="shop-items-list">
            {shopItems.map((item, index) => (
              <motion.div 
                key={index}
                className="shop-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.imageUrl && (
                  <div className="media-image-container">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title || 'Shop item'} 
                      className="media-image"
                    />
                    {item.description && (
                      <div className="media-credit">
                        {item.description}
                      </div>
                    )}
                  </div>
                )}
                <div className="shop-item-details">
                  <h3 className="shop-item-title">{item.title}</h3>
                  {item.price && (
                    <p className="shop-item-price">{item.price}</p>
                  )}
                  {item.purchaseUrl && (
                    <a 
                      href={item.purchaseUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="shop-item-link"
                    >
                      {item.ctaText}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="shop-message">music, merch, and more.</p>
          <a 
            href="https://sanyumusic.bandcamp.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="shop-link"
          >
            visit bandcamp →
          </a>
        </>
      )}
    </div>
  );
}

function SignUpContent() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '/api/send-welcome-email'  // Production uses same domain
        : 'http://10.56.60.117:3001/api/send-welcome-email'; // Local development
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        setMessage('thank you for subscribing!');
        setEmail('');
      } else {
        setMessage('something went wrong. please try again.');
      }
    } catch (error) {
      setMessage('something went wrong. please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <p className="signup-message">
        join the community. be the first to know when i release or perform.
      </p>
      <motion.form 
        className="signup-form"
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your email"
          className="email-input"
          disabled={isSubmitting}
          required
        />
        <button 
          type="submit" 
          className="signup-button"
          disabled={isSubmitting || !email}
        >
          {isSubmitting ? 'subscribing...' : 'subscribe'}
        </button>
      </motion.form>
      {message && (
        <motion.p 
          className="signup-feedback"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {message}
        </motion.p>
      )}
    </>
  );
}

function BackToTopButton() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrolled = document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show button when near bottom of page
      setIsVisible(scrolled > documentHeight - windowHeight * 2);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <div className="back-to-top-container">
      <button onClick={scrollToTop} className="back-to-top-btn">
        back up
      </button>
    </div>
  );
}

function MediaSection() {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadMedia() {
      try {
        const items = await fetchMedia();
        setMediaItems(items);
      } catch (error) {
        console.error('Error loading media:', error);
        setMediaItems([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadMedia();
  }, []);
  
  const openModal = (item) => {
    setSelectedMedia(item);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  if (isLoading) {
    return <div className="loading-state">loading...</div>;
  }

  return (
    <>
      <MediaContent mediaItems={mediaItems} onImageClick={openModal} />

      <AnimatePresence>
        {selectedMedia && (
          <MediaModal 
            media={selectedMedia} 
            onClose={closeModal}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function MediaModal({ media, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <motion.div 
      className="media-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="media-modal-content"
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-image-container">
          {media.type === 'video' ? (
            <iframe
              src={`https://www.youtube.com/embed/${media.url.split('youtu.be/')[1]?.split('?')[0] || media.url.split('watch?v=')[1]?.split('&')[0]}`}
              title={media.title || 'Video'}
              className="modal-video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            media.image && (
              <img 
                src={media.image.startsWith('http') ? media.image : process.env.PUBLIC_URL + media.image}
                alt={media.title || 'Image'}
                className="modal-image"
              />
            )
          )}
      </div>
        
        {/* Intentionally no .modal-details or .modal-credit in zoom view */}
      </motion.div>
    </motion.div>
  );
}

// Main App Component
function AppContent() {
  useEffect(() => {
    // Set custom burgundy spiral cursor
    document.body.style.cursor = `url('${process.env.PUBLIC_URL}/cursor-burgundy.svg'), auto`;
    document.documentElement.style.cursor = `url('${process.env.PUBLIC_URL}/cursor-burgundy.svg'), auto`;
    
    return () => {
      document.body.style.cursor = 'auto';
      document.documentElement.style.cursor = 'auto';
    };
  }, []);

  return (
    <div className="App">
      <AudioPlayer />
      <Navigation />
      <HeroSection />
      
      <Section sectionId="about" title="about">
        <AboutContent />
      </Section>
      
      <Section sectionId="news" title="news">
        <NewsContent />
      </Section>
      
      <Section sectionId="tour" title="tour dates">
        <TourContent />
      </Section>
      
      <Section sectionId="media" title="media">
        <MediaSection />
      </Section>
      
      <Section sectionId="shop" title="shop">
        <ShopContent />
      </Section>
      
      <Section sectionId="signup" title="sign up">
        <SignUpContent />
      </Section>
      
      <footer className="footer">
        <SocialLinks />
      </footer>
      
      <BackToTopButton />
    </div>
  );
}

function App() {
  return (
    <SectionColorProvider>
      <AppContent />
    </SectionColorProvider>
  );
}

export default App;